import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { usePortalAuth } from "../PortalAuthContext";
import { portalPackagesApi } from "../api/packages";
import type { MaterialAudience, PortalRequestsCount } from "../api/types";

// Requests badge — shared outstanding-requests count for the sidebar "Requests"
// nav item (the notifications-unread precedent). Fetched ONCE per shell mount by
// this provider and polled every 30s while a portal session is active, plus a
// catch-up when the tab becomes visible; each nav item reads the shared value
// rather than fetching. Best-effort: a failed count never breaks the chrome, and
// 0 (no provider) means no badge. Applies to EVERY portal type — every recipient
// account has a Requests tab.
//
// The count comes from the WS-1 scalar endpoint (portalPackagesApi.requestsCount)
// so the badge never materializes item rows.

const POLL_INTERVAL_MS = 30_000;

const ZERO: PortalRequestsCount = { needsYou: 0, underReview: 0, total: 0 };

interface RequestsBadge {
  counts: PortalRequestsCount;
  refresh: () => Promise<void>;
}

const RequestsBadgeContext = createContext<RequestsBadge>({
  counts: ZERO,
  refresh: async () => {},
});

/**
 * Which figure the Requests badge shows for a portal type.
 *
 * `total` is the historical behaviour and stays the default, so a portal not
 * named here is untouched by this change. Buyer and seller move to `needsYou`
 * because both Homes now carry a requests strip that speaks in the same terms —
 * a badge counting items Phoenix still owes a review on would contradict the
 * strip sitting a few pixels away, and the point of the split is that those two
 * numbers mean different things.
 *
 * Brokerage is pinned to `total` DELIBERATELY, not by omission: its Home
 * "needs attention" card (BrokerageHomeTab) still phrases the count as
 * "N document requests to complete", which is the claim `needsYou` exists to
 * make honest. Moving the badge without that copy would leave the card and the
 * badge disagreeing. Switching brokerage — and broker / vendor / TPS, which have
 * no Home surface for this at all — is the follow-up this comment exists to
 * make discoverable.
 */
export function requestsBadgeFigureFor(
  accountType: MaterialAudience | undefined,
  counts: PortalRequestsCount,
): number {
  switch (accountType) {
    case "Buyer":
    case "Seller":
      return counts.needsYou;
    case "Brokerage":
      return counts.total;
    default:
      return counts.total;
  }
}

export function PortalRequestsBadgeProvider({ children }: { children: ReactNode }) {
  const auth = usePortalAuth();
  const isAuthed = Boolean(auth.me);
  const [counts, setCounts] = useState<PortalRequestsCount>(ZERO);

  async function refresh() {
    if (!isAuthed) return;
    try {
      setCounts(await portalPackagesApi.requestsCount());
    } catch {
      // Non-critical: a transient failure shouldn't break the chrome.
    }
  }

  useEffect(() => {
    if (!isAuthed) {
      setCounts(ZERO);
      return;
    }
    let cancelled = false;
    const load = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const next = await portalPackagesApi.requestsCount();
        if (!cancelled) setCounts(next);
      } catch {
        // Non-critical; next tick retries.
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    void load();
    const handle = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  return (
    <RequestsBadgeContext.Provider value={{ counts, refresh }}>
      {children}
    </RequestsBadgeContext.Provider>
  );
}

/** Safe outside the provider (tests, pre-auth surfaces): all counts 0, no-op
 *  refresh — the badge and the Home strip just stay off. */
export function useRequestsBadge(): RequestsBadge {
  return useContext(RequestsBadgeContext);
}
